import { DynamicLoginForm, type DynamicLoginStep } from '@Auth/components/DynamicLoginForm';
import { Head, useForm } from '@inertiajs/react';
import { FormEventHandler, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { GuestLayout } from '@/layouts/GuestLayout';

type Props = Diagonal.Auth.PageProps.CreateSessionPageProps & {
  workspace: Diagonal.Workspaces.Data.WorkspaceData;
};

type LoginLayoutProps = Props & {
  children: React.ReactNode;
};

const STEP_DESCRIPTIONS: Record<DynamicLoginStep, string> = {
  email: 'Enter your email to log in',
  verify: 'Enter your password to log in',
  'verify-passwordless': 'Choose how to finish logging in',
  forgot: 'Enter your email to receive a password reset link'
};

function LoginLayout({ children, workspace }: LoginLayoutProps) {
  return <GuestLayout workspace={workspace}>{children}</GuestLayout>;
}

Login.layout = LoginLayout;

export default function Login({
  resourceId,
  passwordResetEnabled,
  magicLinkEnabled,
  devLoginEnabled,
  workspace,
  status
}: Props) {
  const [step, setStep] = useState<DynamicLoginStep>('email');

  const { post: postDevLogin, processing: devLoginProcessing } = useForm({});

  const submitDevLogin: FormEventHandler = (event) => {
    event.preventDefault();

    postDevLogin(`/auth/resource/${resourceId}/dev-login`, {
      preserveScroll: true
    });
  };

  return (
    <>
      <Head title={`Log in to ${workspace.name}`} />
      <Card className="mx-auto max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">
            {step === 'forgot' ? 'Reset your password' : `Login to ${workspace.name}`}
          </CardTitle>
          <CardDescription>{STEP_DESCRIPTIONS[step]}</CardDescription>
        </CardHeader>
        <CardContent>
          <DynamicLoginForm
            resourceId={resourceId}
            passwordResetEnabled={passwordResetEnabled}
            magicLinkEnabled={magicLinkEnabled}
            statusMessage={status}
            onStepChange={setStep}
          />

          {devLoginEnabled && step === 'email' ? (
            <form onSubmit={submitDevLogin} className="mt-4 border-t pt-4">
              <Button
                type="submit"
                variant="outline"
                className="w-full"
                disabled={devLoginProcessing}
              >
                Dev login as admin@godiagonal.com
              </Button>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
